<template>
  <div id="app">
    <!-- Hero Header -->
    <header class="hero-header">
      <div class="logo-container">
        <img alt="PersonalOTP logo" :src="require('@/assets/logo_potp.png')" class="logo" />
        <div class="logo-content">
          <h1 class="logo-text">PersonalOTP.com</h1>
          <p class="tagline">Your personal digital secret handshake.</p>
        </div>
      </div>
    </header>

    <!-- Main OTP Card -->
    <main class="otp-section">
      <div class="otp-card">
        <div class="qr-wrapper" v-if="otpUri">
          <qrcode-vue :value="otpUri" :size="200"></qrcode-vue>
        </div>

        <p class="secret-hint">
          Can't scan the QR? Use this code instead:
          <strong class="highlight">{{ secret }}</strong>.
          Just make sure to send it safely and delete the message right after.
          To enter it manually, go to
          <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener">Google Authenticator</a>
          and hit "Add > Enter a setup key".
        </p>

        <input
          type="text"
          v-model="label"
          placeholder="Unique name (e.g. Alice and Bob secret handshake)"
          v-on:keyup="generateOtpUri"
          class="otp-input"
          aria-label="Name for your shared secret"
        >

        <div class="otp-display">
          <p class="otp-label">Current OTP</p>
          <p class="otp-code">{{ currentOtp }}</p>
          <div class="progress-bar-container">
            <div class="progress-bar-fill" :style="{ width: (progress / 30 * 100) + '%' }"></div>
          </div>
        </div>

        <p class="otp-note">
          Compare this code with the one in your authenticator app. If both you and your partner see matching codes, the setup was successful.
        </p>
      </div>
    </main>

    <!-- FAQ Section -->
    <section class="faq-section" aria-label="Frequently Asked Questions">
      <h2 class="faq-heading">Frequently Asked Questions</h2>

      <details class="faq-item" open>
        <summary class="faq-question">What is PersonalOTP?</summary>
        <div class="faq-answer">
          <p>PersonalOTP helps you create a private, shared secret with someone you trust. By scanning a simple QR code, you and a partner can use any standard authenticator app (like <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener">Google Authenticator</a>) to generate matching 6-digit codes. When you need to confirm you're speaking to the right person, just ask for the code. If it matches yours, you know it's them. It's a modern, digital version of a secret handshake&mdash;perfect for an age of AI voice scams and impersonation.</p>
        </div>
      </details>

      <details class="faq-item">
        <summary class="faq-question">How to Use PersonalOTP</summary>
        <div class="faq-answer">
          <ol class="steps-list">
            <li>
              <strong>Create Your Secret:</strong> Type a unique name for your secret in the box above (e.g., "Mom's emergency code"). This generates a unique QR code.
            </li>
            <li>
              <strong>Share and Scan:</strong> Share this QR code with one other person. You both scan it using an authenticator app (like <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener">Google Authenticator</a>, Microsoft Authenticator, or Authy).
            </li>
            <li>
              <strong>Verify on Demand:</strong> Now your apps will generate the same 6-digit code, which refreshes every 30 seconds. To verify the other person's identity, simply ask them to read the current code. If it matches yours, you can be sure it's them.
            </li>
          </ol>
        </div>
      </details>

      <details class="faq-item">
        <summary class="faq-question">Why do I need a digital secret handshake?</summary>
        <div class="faq-answer">
          <p>In an era of AI voice cloning and sophisticated scams, it's getting harder to trust who you're talking to online or on the phone. A shared secret code that only you and one other person can generate provides a quick and secure way to verify identity.</p>
          <div class="use-case">
            <h4>Protecting Your Family</h4>
            <p>Imagine getting a frantic call from a loved one asking for urgent financial help. Scammers can now fake voices and spoof phone numbers. Instead of panicking, you can say, "What's our code?" If they can't provide the matching 6-digit number, you'll know it's a scam.</p>
          </div>
          <div class="use-case">
            <h4>Secure Business Conversations</h4>
            <p>If you need to discuss sensitive information with a colleague or client, you can start the conversation by verifying their identity with your shared OTP. This ensures you're not leaking confidential data to an impersonator.</p>
          </div>
        </div>
      </details>

      <details class="faq-item">
        <summary class="faq-question">Do we both need an authenticator app?</summary>
        <div class="faq-answer">
          <p>Yes. Both parties need a standard authenticator app installed. These apps generate the Time-based One-Time Passwords (TOTPs) that you'll compare. Popular free options include <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener">Google Authenticator</a>, <a href="https://www.microsoft.com/en-us/security/mobile-authenticator-app" target="_blank" rel="noopener">Microsoft Authenticator</a>, and <a href="https://authy.com/" target="_blank" rel="noopener">Authy</a>.</p>
        </div>
      </details>

      <details class="faq-item">
        <summary class="faq-question">Is PersonalOTP secure?</summary>
        <div class="faq-answer">
          <p>Yes. PersonalOTP uses the same industry-standard TOTP (Time-Based One-Time Password) algorithm that secures your accounts with two-factor authentication (2FA). The secret is generated entirely in your browser and is never sent to our servers.</p>
        </div>
      </details>

      <details class="faq-item">
        <summary class="faq-question">What if I lose my phone or delete the authenticator app?</summary>
        <div class="faq-answer">
          <p>If you lose access to your authenticator app, the shared secret is lost forever. You'll need to create and share a new secret with your contact. This is a security feature: if someone gets access to your old device, they can't impersonate you.</p>
        </div>
      </details>

      <details class="faq-item">
        <summary class="faq-question">What happens if someone else scans our QR code?</summary>
        <div class="faq-answer">
          <p>The QR code contains the secret. If you suspect an unauthorized person has scanned it, immediately generate a new one and share it again with your trusted contact. Once the new code is scanned, the old one becomes irrelevant.</p>
        </div>
      </details>

      <details class="faq-item">
        <summary class="faq-question">How do I safely share the QR code?</summary>
        <div class="faq-answer">
          <p>The safest way is to have the other person scan the QR code directly from your screen in person. If you must share it remotely, use an end-to-end encrypted messaging app. After they've scanned it, delete the QR code image from the conversation history if possible.</p>
        </div>
      </details>
    </section>

    <footer class="site-footer">
      <p>An experiment by <a href="https://x.com/arturoleon" target="_blank" rel="noopener">@arturoleon</a></p>
    </footer>
  </div>
</template>

<script>
import { TOTP, Secret } from 'otpauth';
import QrcodeVue from 'qrcode.vue';
import 'bulma/css/bulma.css'

export default {
  name: 'App',
  components: {
    QrcodeVue,
  },
  data() {
    return {
      otpUri: '',
      label: '',
      totp: null,
      currentOtp: '',
      progress: 0,
      secret: ''
    };
  },
  mounted() {
    this.generateOtpUri();
    this.startOtpRefresh();
  },
  methods: {
    generateOtpUri() {
      // Generate a new TOTP instance
      this.totp = new TOTP({
        issuer: 'PersonalOTP',
        label: this.label,
        algorithm: 'SHA1',
        digits: 6,
        period: 30,
        secret: new Secret({size: 10}),
      });
      // Generate a provisioning URI
      this.otpUri = this.totp.toString();

      this.secret = this.totp.secret.base32.match(/.{1,4}/g).join(' ');

      // Generate the first OTP
      this.generateOtp();
    },
    generateOtp() {
      // Generate the OTP from the TOTP instance
      if (this.totp) {
        let currentOtp = this.totp.generate()
        this.currentOtp = currentOtp.substring(0, 3) + ' ' + currentOtp.substring(3);
      }
    },
    startOtpRefresh() {
      // Calculate the time remaining until the next 30-second period
      const now = Date.now();
      const period = 30000;  // 30 seconds
      const timeUntilNextPeriod = period - (now % period);
      this.progress = 30 - (timeUntilNextPeriod / 1000);

      // Start the timer after the time until the next period
      setTimeout(() => {
        this.generateOtp();
        this.progress = 0;  // Reset the progress

        // Then start the interval
        this.otpRefreshInterval = setInterval(() => {
          this.generateOtp();
          this.progress = 0;  // Reset the progress
        }, period);
      }, timeUntilNextPeriod);

        // Start the progress interval
        this.progressInterval = setInterval(() => {
          this.progress++;  // Increase the progress
        }, 1000);
    },
  },
};
</script>

<style scoped>
/* ===== Base & Layout ===== */
#app {
  font-family: "Lato", sans-serif;
  color: #2c3e50;
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ===== Hero Header ===== */
.hero-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.logo {
  height: 7rem;
  flex-shrink: 0;
}

.logo-content {
  text-align: left;
}

.logo-text {
  font-family: "Lato", sans-serif;
  font-weight: 900;
  font-size: 3.2rem;
  color: #1a1852;
  line-height: 1.1;
  margin: 0;
}

.tagline {
  font-size: 1.05rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

/* ===== OTP Card ===== */
.otp-section {
  margin-bottom: 3rem;
}

.otp-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 2.5rem 2rem;
  box-shadow: 0 4px 24px rgba(26, 24, 82, 0.06);
  text-align: center;
}

.qr-wrapper {
  display: inline-block;
  padding: 1rem;
  background: #fff;
  border-radius: 12px;
  border: 2px solid #f0f0f5;
  margin-bottom: 1.5rem;
}

.secret-hint {
  font-size: 0.9rem;
  color: #6b7280;
  line-height: 1.6;
  max-width: 540px;
  margin: 0 auto 1.5rem;
}

.secret-hint a {
  color: #6c63ff;
  text-decoration: none;
}

.secret-hint a:hover {
  text-decoration: underline;
}

.highlight {
  background-color: #f3f2ff;
  border-radius: 6px;
  padding: 2px 8px;
  border: 1px solid #d8d6f7;
  color: #1a1852;
  font-family: "Courier New", monospace;
  font-size: 0.95em;
  word-break: break-all;
}

.otp-input {
  display: block;
  width: 100%;
  max-width: 480px;
  margin: 0 auto 2rem;
  padding: 0.85rem 1.2rem;
  font-size: 1rem;
  font-family: "Lato", sans-serif;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;
}

.otp-input:focus {
  border-color: #6c63ff;
  box-shadow: 0 0 0 3px rgba(108, 99, 255, 0.12);
}

.otp-input::placeholder {
  color: #9ca3af;
}

/* OTP Display */
.otp-display {
  background: linear-gradient(135deg, #1a1852 0%, #2d2a6e 100%);
  border-radius: 14px;
  padding: 1.5rem 2rem 1rem;
  max-width: 320px;
  margin: 0 auto 1rem;
}

.otp-label {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.25rem;
}

.otp-code {
  font-size: 3rem;
  font-weight: 900;
  color: #ffffff;
  letter-spacing: 0.15em;
  font-family: "Lato", sans-serif;
  margin: 0;
  line-height: 1.2;
}

.progress-bar-container {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  height: 4px;
  margin-top: 1rem;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: #6c63ff;
  border-radius: 4px;
  transition: width 1s linear;
}

.otp-note {
  font-size: 0.85rem;
  color: #9ca3af;
  max-width: 420px;
  margin: 0 auto;
  line-height: 1.5;
}

/* ===== FAQ Section ===== */
.faq-section {
  margin-bottom: 3rem;
}

.faq-heading {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1852;
  text-align: center;
  margin-bottom: 1.5rem;
}

.faq-item {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 0.75rem;
  overflow: hidden;
  transition: box-shadow 0.2s ease;
}

.faq-item[open] {
  box-shadow: 0 2px 12px rgba(26, 24, 82, 0.05);
}

.faq-question {
  font-size: 1rem;
  font-weight: 700;
  color: #1a1852;
  padding: 1.1rem 1.5rem;
  cursor: pointer;
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
}

.faq-question::-webkit-details-marker {
  display: none;
}

.faq-question::after {
  content: "+";
  font-size: 1.4rem;
  font-weight: 300;
  color: #6c63ff;
  flex-shrink: 0;
  margin-left: 1rem;
  transition: transform 0.2s ease;
}

.faq-item[open] > .faq-question::after {
  content: "\2212";
}

.faq-answer {
  padding: 0 1.5rem 1.25rem;
  color: #4b5563;
  line-height: 1.7;
  font-size: 0.95rem;
}

.faq-answer p {
  margin: 0 0 0.75rem;
}

.faq-answer p:last-child {
  margin-bottom: 0;
}

.faq-answer a {
  color: #6c63ff;
  text-decoration: none;
}

.faq-answer a:hover {
  text-decoration: underline;
}

.steps-list {
  padding-left: 1.25rem;
  margin: 0;
}

.steps-list li {
  margin-bottom: 0.75rem;
}

.steps-list li:last-child {
  margin-bottom: 0;
}

.use-case {
  background: #f9fafb;
  border-left: 3px solid #6c63ff;
  border-radius: 0 8px 8px 0;
  padding: 1rem 1.25rem;
  margin-top: 1rem;
}

.use-case h4 {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1a1852;
  margin: 0 0 0.4rem;
}

.use-case p {
  margin: 0;
}

/* ===== Footer ===== */
.site-footer {
  margin-top: auto;
  text-align: center;
  padding: 2rem 1rem;
  color: #9ca3af;
  font-size: 0.9rem;
  border-top: 1px solid #f0f0f5;
}

.site-footer a {
  color: #6c63ff;
  text-decoration: none;
  font-weight: 600;
}

.site-footer a:hover {
  text-decoration: underline;
}

/* ===== Responsive ===== */
@media (max-width: 600px) {
  #app {
    padding: 1.25rem 1rem;
  }

  .logo-container {
    flex-direction: column;
    gap: 0.5rem;
  }

  .logo-content {
    text-align: center;
  }

  .logo {
    height: 4.5rem;
  }

  .logo-text {
    font-size: 2.2rem;
  }

  .tagline {
    font-size: 0.95rem;
  }

  .otp-card {
    padding: 1.5rem 1rem;
    border-radius: 12px;
  }

  .otp-code {
    font-size: 2.4rem;
  }

  .otp-display {
    padding: 1.25rem 1.5rem 0.75rem;
  }

  .faq-question {
    padding: 1rem 1.25rem;
    font-size: 0.95rem;
  }

  .faq-answer {
    padding: 0 1.25rem 1rem;
  }
}
</style>
